import SeriesModel from 'echarts/lib/model/Series';
import type SeriesData from 'echarts/lib/data/SeriesData';
import type GlobalModel from 'echarts/lib/model/Global';
import Radar from 'echarts/lib/coord/radar/Radar';
import createSeriesDataSimply from 'echarts/lib/chart/helper/createSeriesDataSimply';
import {
  SeriesOption,
  LineStyleOption,
  SeriesLabelOption,
  SymbolOptionMixin,
  ItemStyleOption,
  AreaStyleOption,
  OptionDataValue,
  StatesOptionMixin,
  OptionDataItemObject,
  SeriesEncodeOptionMixin,
  CallbackDataParams,
  DefaultStatesMixinEmphasis
} from 'echarts/lib/util/types';
import { PathStyleProps } from 'zrender';
import * as zrUtil from 'zrender/src/core/util';
import LegendVisualProvider from 'echarts/lib/visual/LegendVisualProvider';
import {
  createTooltipMarkup,
  retrieveVisualColorForTooltipMarker
} from 'echarts/lib/component/tooltip/tooltipMarkup';
import ExtensionAPI from 'echarts/lib/core/ExtensionAPI';

type dvRadarSeriesDataValue = OptionDataValue[];

interface DvRadarStatesMixin {
  emphasis?: DefaultStatesMixinEmphasis;
}

// 扇形选择器
interface SectorSelectorOption {
  show?: boolean; // 是否显示
  lineStyle?: LineStyleOption; // 指针线的样式
  sectorStyle?: PathStyleProps; // 扇形区域的样式
  symbolStyle?: PathStyleProps; // 点的样式
  pointerOffset?: number; // 指针伸出坐标系的长度
}

// 定义dvRadar配置项
export interface DvRadarSeriesStateOption<TCbParams = never> {
  lineStyle?: LineStyleOption;
  areaStyle?: AreaStyleOption;
  label?: SeriesLabelOption;
  itemStyle?: ItemStyleOption<TCbParams>;
  // TODO: 曲线模式
  smooth?: number;
  sectorSelector?: SectorSelectorOption;
  dvClockWise?: boolean;
}
export interface DvRadarSeriesDataItemOption
  extends SymbolOptionMixin,
    DvRadarSeriesStateOption<CallbackDataParams>,
    StatesOptionMixin<DvRadarSeriesStateOption<CallbackDataParams>, DvRadarStatesMixin>,
    OptionDataItemObject<dvRadarSeriesDataValue> {
}

export interface DvRadarSeriesOption
  extends SeriesOption<DvRadarSeriesStateOption, DvRadarStatesMixin>,
    DvRadarSeriesStateOption,
    SymbolOptionMixin<CallbackDataParams>,
    SeriesEncodeOptionMixin {
  type?: 'dvRadar';
  coordinateSystem?: 'radar'

  radarIndex?: number;
  radarId?: string;

  data?: (DvRadarSeriesDataItemOption | dvRadarSeriesDataValue)[];
}

// eslint-disable-next-line no-use-before-define
export type DvRadarSeriesModelClazz = DvRadarSeriesModel & SeriesModel<DvRadarSeriesOption>;

class DvRadarSeriesModel extends SeriesModel<DvRadarSeriesOption> {
  static readonly type = 'series.dvRadar';

  type = DvRadarSeriesModel.type;

  static readonly dependencies = ['radar'];

  coordinateSystem: Radar;
  
  hasSymbolVisual = true;

  // Overwrite
  init(this: DvRadarSeriesModelClazz, option: DvRadarSeriesOption, api: ExtensionAPI ) {
    super.init.apply(this, arguments as any);

    // Enable legend selection for each data item
    // Use a function instead of direct access because data reference may changed
    this.legendVisualProvider = new LegendVisualProvider(
      zrUtil.bind(this.getData, this),
      zrUtil.bind(this.getRawData, this)
    );
  }

  getInitialData(
    this: DvRadarSeriesModelClazz,
    option: DvRadarSeriesOption,
    ecModel: GlobalModel
  ): SeriesData {
    // eslint-disable-next-line no-console
    return createSeriesDataSimply(this, {
      generateCoord: 'indicator_',
      generateCoordCount: Infinity
    });
  }

  formatTooltip(
    this: DvRadarSeriesModelClazz,
    dataIndex: number,
    multipleSeries?: boolean,
    dataType?: string
  ) {
    const data = this.getData();
    const coordSys = this.coordinateSystem;
    const indicatorAxes = coordSys.getIndicatorAxes();
    const name = this.getData().getName(dataIndex);
    const nameToDisplay = name === '' ? this.name : name;
    const markerColor = retrieveVisualColorForTooltipMarker(this, dataIndex);

    return createTooltipMarkup('section', {
      header: nameToDisplay,
      sortBlocks: true,
      blocks: zrUtil.map(indicatorAxes, axis => {
        const val = data.get(data.mapDimension(axis.dim), dataIndex);
        return createTooltipMarkup('nameValue', {
          markerType: 'subItem',
          markerColor: markerColor,
          name: axis.name,
          value: val,
          sortParam: val
        });
      })
    });
  }

  getTooltipPosition(this: DvRadarSeriesModelClazz, dataIndex: number) {
    if (dataIndex != null) {
      const data = this.getData();
      const coordSys = this.coordinateSystem;
      const values = data.getValues(
        zrUtil.map(coordSys.dimensions, function (dim) {
          return data.mapDimension(dim);
        }),
        dataIndex
      );

      for (let i = 0, len = values.length; i < len; i++) {
        if (!isNaN(values[i] as number)) {
          const indicatorAxes = coordSys.getIndicatorAxes();
          return coordSys.coordToPoint(indicatorAxes[i].dataToCoord(values[i]), i);
        }
      }
    }
  }

  static defaultOption: DvRadarSeriesOption = {
    z: 0,
    colorBy: 'data',
    coordinateSystem: 'radar',
    legendHoverLink: true,
    radarIndex: 0,
    lineStyle: {
      width: 2,
      type: 'solid',
      join: 'round'
    },
    label: {
      position: 'top'
    },
    symbolSize: 8,
    animation: false,
    smooth: 0,
    dvClockWise: true,
  };
}

export default DvRadarSeriesModel;
